import React, { useState, useEffect } from "react";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  FilterSection,
  SectionTitle,
  SectionActions,
  SelectAllButton,
  DateInputGroup,
  DateInput,
  DateError,
  CompanyInputGroup,
  CompanyInput,
  CompanyList,
  CompanyItem,
  CompanyIcon,
  CompanyCheckbox,
  ModalFooter,
  ClearButton,
  ApplyButton,
} from "./FilterModalStyles";
import { FiX, FiCalendar, FiSearch, FiAlertCircle, FiCheck } from "react-icons/fi";
import { GoOrganization } from "react-icons/go";

export default function FilterModal({ isOpen, onClose, onApply }) {
  // Datas pré-preenchidas
  const [startDate, setStartDate] = useState("01/01/2024");
  const [endDate, setEndDate] = useState("31/12/2024");
  const [searchCompany, setSearchCompany] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState(["Empresa ABC Ltda"]);
  
  // Estados de erro
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");

  // Mock de empresas com cores - substituir por dados reais
  const mockCompanies = [
    { name: "Empresa ABC Ltda", color: "#10b981", cnpj: "71444475000115" },
    { name: "Empresa XYZ S.A.", color: "#ec4899", cnpj: "27221841000185" },
    { name: "Tech Solutions Inc", color: "#f59e0b", cnpj: "06185580000153" },
    { name: "Digital Corp", color: "#8b5cf6", cnpj: "85151504000165" },
    { name: "Innovation Labs", color: "#3b82f6", cnpj: "44506640000143" },
    { name: "Global Services", color: "#ef4444", cnpj: "54332671000189" },
  ];

  // Toggle seleção de empresa
  const toggleCompany = (companyName) => {
    setSelectedCompanies((prev) => {
      if (prev.includes(companyName)) {
        return prev.filter((name) => name !== companyName);
      } else {
        return [...prev, companyName];
      }
    });
  };

  // Selecionar/Desselecionar todas
  const toggleSelectAll = () => {
    if (selectedCompanies.length === filteredCompanies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(filteredCompanies.map((c) => c.name));
    }
  };

  // Formatar data automaticamente (DD/MM/AAAA)
  const formatDate = (value) => {
    const numbers = value.replace(/\D/g, "");
    const limited = numbers.slice(0, 8);
    
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 4) {
      return `${limited.slice(0, 2)}/${limited.slice(2)}`;
    } else {
      return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
    }
  };

  // Validar se a data é válida
  const isValidDate = (dateString) => {
    if (dateString.length !== 10) return false;
    
    const [day, month, year] = dateString.split("/").map(Number);
    
    if (!day || !month || !year) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;
    
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) return false;
    
    return true;
  };

  // Converter string de data para objeto Date
  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split("/").map(Number);
    return new Date(year, month - 1, day);
  };

  // Validar data inicial
  const validateStartDate = (date) => {
    if (!date) {
      setStartDateError("Data inicial é obrigatória");
      return false;
    }
    
    if (date.length === 10 && !isValidDate(date)) {
      setStartDateError("Data inválida");
      return false;
    }
    
    if (date.length === 10 && endDate.length === 10 && isValidDate(date) && isValidDate(endDate)) {
      const start = parseDate(date);
      const end = parseDate(endDate);
      
      if (start > end) {
        setStartDateError("Data inicial não pode ser maior que a final");
        return false;
      }
    }
    
    setStartDateError("");
    return true;
  };

  // Validar data final
  const validateEndDate = (date) => {
    if (!date) {
      setEndDateError("Data final é obrigatória");
      return false;
    }
    
    if (date.length === 10 && !isValidDate(date)) {
      setEndDateError("Data inválida");
      return false;
    }
    
    if (date.length === 10 && startDate.length === 10 && isValidDate(date) && isValidDate(startDate)) {
      const start = parseDate(startDate);
      const end = parseDate(date);
      
      if (end < start) {
        setEndDateError("Data final não pode ser menor que a inicial");
        return false;
      }
    }
    
    setEndDateError("");
    return true;
  };

  const handleStartDateChange = (e) => {
    const formatted = formatDate(e.target.value);
    setStartDate(formatted);
    
    if (formatted.length === 10) {
      validateStartDate(formatted);
    } else {
      setStartDateError("");
    }
  };

  const handleEndDateChange = (e) => {
    const formatted = formatDate(e.target.value);
    setEndDate(formatted);
    
    if (formatted.length === 10) {
      validateEndDate(formatted);
    } else {
      setEndDateError("");
    }
  };

  // Revalidar quando uma data muda
  useEffect(() => {
    if (startDate.length === 10) {
      validateStartDate(startDate);
    }
  }, [endDate]);

  useEffect(() => {
    if (endDate.length === 10) {
      validateEndDate(endDate);
    }
  }, [startDate]);

  // Selecionar todo o texto ao clicar
  const handleDateClick = (e) => {
    e.target.select();
  };

  const handleApply = () => {
    const isStartValid = validateStartDate(startDate);
    const isEndValid = validateEndDate(endDate);
    
    if (!isStartValid || !isEndValid) {
      return;
    }
    
    onApply({
      startDate,
      endDate,
      companies: selectedCompanies,
    });
    onClose();
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setSearchCompany("");
    setSelectedCompanies([]);
    setStartDateError("");
    setEndDateError("");
  };

  if (!isOpen) return null;

  const filteredCompanies = mockCompanies.filter((company) =>
    company.name.toLowerCase().includes(searchCompany.toLowerCase())
  );

  const allSelected = selectedCompanies.length === filteredCompanies.length && filteredCompanies.length > 0;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FiSearch />
            <span>Filtros</span>
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FilterSection>
            <SectionTitle>
              <FiCalendar />
              <span>Período</span>
            </SectionTitle>
            <DateInputGroup>
              <div className="input-wrapper">
                <label>Data Inicial</label>
                <DateInput
                  type="text"
                  placeholder="DD/MM/AAAA"
                  value={startDate}
                  onChange={handleStartDateChange}
                  onClick={handleDateClick}
                  maxLength={10}
                  hasError={!!startDateError}
                />
                {startDateError && (
                  <DateError>
                    <FiAlertCircle />
                    <span>{startDateError}</span>
                  </DateError>
                )}
              </div>
              <div className="separator">até</div>
              <div className="input-wrapper">
                <label>Data Final</label>
                <DateInput
                  type="text"
                  placeholder="DD/MM/AAAA"
                  value={endDate}
                  onChange={handleEndDateChange}
                  onClick={handleDateClick}
                  maxLength={10}
                  hasError={!!endDateError}
                />
                {endDateError && (
                  <DateError>
                    <FiAlertCircle />
                    <span>{endDateError}</span>
                  </DateError>
                )}
              </div>
            </DateInputGroup>
          </FilterSection>

          <FilterSection>
            <SectionActions>
              <SectionTitle>
                <GoOrganization />
                <span>Empresas ({selectedCompanies.length})</span>
              </SectionTitle>
              <SelectAllButton onClick={toggleSelectAll}>
                {allSelected ? "Desselecionar todas" : "Selecionar todas"}
              </SelectAllButton>
            </SectionActions>
            <CompanyInputGroup>
              <FiSearch className="search-icon" />
              <CompanyInput
                type="text"
                placeholder="Buscar empresa..."
                value={searchCompany}
                onChange={(e) => setSearchCompany(e.target.value)}
              />
            </CompanyInputGroup>
            <CompanyList>
              {filteredCompanies.map((company, index) => {
                const isSelected = selectedCompanies.includes(company.name);
                return (
                  <CompanyItem
                    key={index}
                    isSelected={isSelected}
                    onClick={() => toggleCompany(company.name)}
                  >
                    <CompanyCheckbox isSelected={isSelected}>
                      {isSelected && <FiCheck />}
                    </CompanyCheckbox>
                    <CompanyIcon color={company.color}>
                      <GoOrganization />
                    </CompanyIcon>
                    <div className="company-info">
                      <span className="company-name">{company.name}</span>
                      <span className="company-cnpj">{company.cnpj}</span>
                    </div>
                  </CompanyItem>
                );
              })}
            </CompanyList>
          </FilterSection>
        </ModalBody>

        <ModalFooter>
          <ClearButton onClick={handleClear}>Limpar Filtros</ClearButton>
          <ApplyButton onClick={handleApply}>Aplicar Filtros</ApplyButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
}
